import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CursorPosition, useCursorPosition } from './Cursor';
import { StackedDataPosition, StackedTimeSeriesData } from './dataTypes';
import { translateCursorPositionToDataIndex } from './useCursorDateIndex';
import { translateCursorPositionToStackIndex } from './useCursorStackIndex';

/**
 * Given a cursor position and a data set, return the data associated.
 * @param cursor A CursorPosition (usually from useCursorPosition).
 * @param data The timeseries data.
 * @returns A StackedDataPosition which is translated into the dataset space.
 */
export function translateCursorToStackedDataPosition<
  Data extends StackedTimeSeriesData,
>(
  cursor: CursorPosition,
  data: Data[] | undefined,
): StackedDataPosition<Data> | undefined {
  const dataIndex = translateCursorPositionToDataIndex({
    cursor,
    data,
    getTimestamp: (datapoint) => datapoint.timestamp,
  });
  const datapoint = dataIndex !== null ? data?.[dataIndex] : undefined;
  const stackIndex = translateCursorPositionToStackIndex({
    cursor,
    datapoint,
  });
  return dataIndex !== null && datapoint
    ? {
        dataIndex,
        stackIndex,
        data: datapoint,
      }
    : undefined;
}

const usePositionTranslator = <Data extends StackedTimeSeriesData>(
  data: Data[],
) => {
  return useCallback(
    (cursor: CursorPosition) =>
      translateCursorToStackedDataPosition(cursor, data),
    [data],
  );
};

/**
 * Toolkit for translating from coordinate (x,y) space to data space. Includes:
 * 1. a React context to hold the translated value.
 * 2. a context provider for setting and holding the translated value.
 * 3. a consumer hook to read the translated value.
 * Using context here allows child components to subscribe to the current data
 * point being held by the cursor, e.g. in a Tooltip.
 */

const TranslatedCursorPositionContext = createContext<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  position?: StackedDataPosition<any>;
}>({ position: undefined });

export const TranslatedCursorPositionProvider = <
  Data extends StackedTimeSeriesData,
>({
  data,
  children,
}: {
  data: Data[];
  children: React.ReactNode;
}) => {
  const [positionData, setPositionData] = useState<Data>();
  const [dataIndex, setDataIndex] = useState<number>();
  const [stackIndex, setStackIndex] = useState<number | null>();

  const cursor = useCursorPosition();
  const translatePosition = usePositionTranslator(data);
  useEffect(() => {
    const position = translatePosition(cursor);
    setPositionData(position?.data);
    setDataIndex(position?.dataIndex);
    setStackIndex(position?.stackIndex);
  }, [cursor, translatePosition]);

  const value = useMemo(() => {
    return dataIndex !== undefined &&
      stackIndex !== undefined &&
      positionData !== undefined
      ? { position: { dataIndex, stackIndex, data: positionData } }
      : { position: undefined };
  }, [dataIndex, positionData, stackIndex]);

  return (
    <TranslatedCursorPositionContext.Provider value={value}>
      {children}
    </TranslatedCursorPositionContext.Provider>
  );
};

export const useTranslatedCursorPosition = <
  Data extends StackedTimeSeriesData,
>() => {
  const context = useContext(TranslatedCursorPositionContext);
  if (!context) {
    throw new Error('Expected a translation provider');
  }
  return context.position as StackedDataPosition<Data>;
};
